import { EGroupItemType, EGroupType, Group, GroupItem } from '@/models';
import {
  ALL_WEEK_DAYS,
  ALL_DAY_TIME_INTERVALS,
} from '@/constants/features/schedule';
import { IRuleFormDraft } from '@/features/groups/RuleFormPopup/RuleFormPopup.types';

// the extension matches keywords case-insensitively, so "Porn" and "porn" are the same rule
const getComparableItemValue = (item: GroupItem): string =>
  item.itemType === EGroupItemType.keyword
    ? item.itemValue.trim().toLowerCase()
    : item.itemValue;

// in whitelist mode the extension allows only enabled groups, and usage limit
// items only after the limit is reached, so they are left out
export const getWhitelistAllowedItems = (
  groups: readonly Group[]
): GroupItem[] =>
  groups
    .filter(group => group.enabled && group.type === EGroupType.schedule)
    .flatMap(group => group.items);

// the extension blocks only with enabled schedule and usage limit groups
export const getActiveBlockingItems = (groups: readonly Group[]): GroupItem[] =>
  groups
    .filter(
      group =>
        group.enabled &&
        (group.type === EGroupType.schedule ||
          group.type === EGroupType.usageLimit)
    )
    .flatMap(group => group.items);

export const isSameGroupItem = (first: GroupItem, second: GroupItem): boolean =>
  first.itemType === second.itemType &&
  getComparableItemValue(first) === getComparableItemValue(second);

export const buildGroupFromDraft = (
  draft: IRuleFormDraft,
  initialGroup: Group | undefined,
  newGroupId: string
): Group => {
  const now = Date.now();
  return {
    id: initialGroup?.id ?? newGroupId,
    name: draft.name.trim(),
    type: initialGroup?.type ?? EGroupType.schedule,
    icon: draft.icon,
    color: initialGroup?.color,
    enabled: draft.enabled,
    schedule: {
      activeDays: draft.activeDays,
      timeIntervals: draft.timeIntervals,
    },
    items: draft.items,
    originDeviceId: initialGroup?.originDeviceId ?? null,
    updatedAt: now,
    createdAt: initialGroup?.createdAt ?? now,
  };
};

// an empty group is always the schedule/focusTime placeholder the backend seeds
// per user - there is no user flow that creates one. delete the placeholder
// helpers and their call sites once the backend stops seeding placeholders.
export const isPlaceholderDefaultGroup = (group: Group): boolean =>
  group.items.length === 0;

export const excludePlaceholderDefaultGroups = (groups: Group[]): Group[] =>
  groups.filter(group => !isPlaceholderDefaultGroup(group));

export const findPlaceholderGroup = (
  groups: Group[],
  type: EGroupType
): Group | undefined =>
  groups.find(group => group.type === type && isPlaceholderDefaultGroup(group));

// merges a candidate into an existing placeholder of the same type when one
// exists, instead of appending it as a separate group.
export interface IResolvedGroupToSave {
  readonly group: Group;
  readonly reusesPlaceholder: boolean;
}

export const resolveGroupToSave = (
  candidate: Group,
  existingGroups: Group[]
): IResolvedGroupToSave => {
  const placeholder = findPlaceholderGroup(existingGroups, candidate.type);
  if (!placeholder) {
    return { group: candidate, reusesPlaceholder: false };
  }

  return {
    group: {
      ...candidate,
      id: placeholder.id,
      originDeviceId: placeholder.originDeviceId,
    },
    reusesPlaceholder: true,
  };
};

export const buildGenericScheduleGroup = (
  item: GroupItem,
  name: string
): Group => {
  const now = Date.now();
  return {
    id: crypto.randomUUID(),
    name,
    type: EGroupType.schedule,
    enabled: true,
    schedule: {
      activeDays: ALL_WEEK_DAYS,
      timeIntervals: ALL_DAY_TIME_INTERVALS,
    },
    items: [item],
    originDeviceId: null,
    updatedAt: now,
    createdAt: now,
  };
};
